import { EnvironmentOutlined } from "@ant-design/icons"
import { Checkbox, Col, Row, Select } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { AddressAutoComplete } from "../../../../components/AutoComplete/AddressAutoComplete.tsx"
import { suggestionZip } from "../../../../components/AutoComplete/suggestion-zip.ts"
import { ZipLookupHint } from "../../../../components/AutoComplete/ZipLookupHint.tsx"
import { AddressMapPreview } from "../../../../components/Shared/AddressMapPreview.tsx"
import { AddressQuickLinks } from "../../../../components/Shared/AddressQuickLinks.tsx"
import { useKnownStates } from "../../../../hooks/use-known-states.ts"
import { useZipLookup } from "../../../../hooks/use-zip-lookup.ts"
import { zipCodeRegexUS } from "../../../../utils/validation/zip-code.ts"
import { WorkspaceCard } from "../../../Lead/LeadWorkspacePage/cards/WorkspaceCard.tsx"
import { pickChangedFields } from "../../../Lead/LeadWorkspacePage/lead-workspace-patch.ts"
import { LeadOverviewField } from "../../../Lead/LeadWorkspacePage/overview/LeadOverviewField.tsx"
import { OverviewCardTitle } from "../../../Lead/LeadWorkspacePage/overview/OverviewCardIcon.tsx"
import { isLinkedProjectCustomer } from "./project-overview-customer.ts"
import type { Project, ProjectUpdateData } from "../../types/project.types.ts"
import {
  type AddressLike,
  customerAddressLine,
  isCustomerAddressLocked,
  projectCustomerAddressValues,
  projectMapAddress,
  projectPropertyAddressValues,
  propertyAddressLine
} from "./project-overview-location.ts"
import { TextInput } from "../../../../components/Input/TextInput.tsx"

// project.types.ts predates use_customer_address being wired up, but the API
// has always returned it (PropertyInformationSection already watches it off
// the same initial project payload) — this is a read-side type fix.
type ProjectWithAddressExtras = Project & { use_customer_address?: boolean }

interface LocationCardValues {
  customer: AddressLike
  address: string | null
  address_2: string | null
  city: string | null
  state: string | null
  zip_code: string | null
  use_customer_address: boolean
}

interface ProjectLocationCardProps {
  project: ProjectWithAddressExtras
  filterOptions: Record<string, any>
  canEdit?: boolean
  onSave: (changed: Partial<ProjectUpdateData>) => Promise<boolean>
}

interface AddressEditorProps {
  title: string
  values: AddressLike
  disabled: boolean
  knownStates: string[]
  stateOptions: Record<string, any>["state"]
  onChange: (patch: Partial<AddressLike>) => void
  ariaPrefix?: string
  autoFocus?: boolean
}

function AddressEditor({
  title,
  values,
  disabled,
  knownStates,
  stateOptions,
  onChange,
  ariaPrefix,
  autoFocus
}: AddressEditorProps) {
  const { t } = useTranslation()
  const ariaLabel = (label: string) =>
    ariaPrefix ? `${ariaPrefix} ${label}` : label

  const {
    lookupZip,
    checkCity,
    chooseMatch,
    declineCityMismatch,
    suppressLookupFor,
    status
  } = useZipLookup({
    knownStates,
    disabled,
    initialZip: values.zip_code,
    onFill: onChange
  })

  return (
    <>
      <Col xs={24}>
        <b>{title}</b>
      </Col>
      <Col xs={24}>
        <LeadOverviewField
          label={t("projectWorkspace.cards.location.addressSearch")}
        >
          <AddressAutoComplete
            disabled={disabled}
            placeholder={`Search ${title.toLowerCase()}...`}
            onSelect={({ address, city_zips }) => {
              const zip = suggestionZip({ address, city_zips })
              suppressLookupFor(zip)
              onChange({
                address: address?.street || null,
                city: address?.city || null,
                state: address?.state || null,
                zip_code: zip
              })
              checkCity(zip, address?.city)
            }}
          />
        </LeadOverviewField>
      </Col>
      <Col xs={24} sm={12}>
        <LeadOverviewField label={t("projectWorkspace.cards.location.address")}>
          <TextInput
            autoFocus={autoFocus}
            disabled={disabled}
            aria-label={ariaLabel(t("projectWorkspace.cards.location.address"))}
            value={values.address ?? ""}
            onChange={(event) =>
              onChange({ address: event.target.value || null })
            }
          />
        </LeadOverviewField>
      </Col>
      <Col xs={24} sm={12}>
        <LeadOverviewField
          label={t("projectWorkspace.cards.location.address2")}
        >
          <TextInput
            disabled={disabled}
            aria-label={ariaLabel(
              t("projectWorkspace.cards.location.address2")
            )}
            value={values.address_2 ?? ""}
            onChange={(event) =>
              onChange({ address_2: event.target.value || null })
            }
          />
        </LeadOverviewField>
      </Col>
      <Col xs={24} sm={8}>
        <LeadOverviewField label={t("projectWorkspace.cards.location.city")}>
          <TextInput
            disabled={disabled}
            aria-label={ariaLabel(t("projectWorkspace.cards.location.city"))}
            value={values.city ?? ""}
            onChange={(event) => {
              onChange({ city: event.target.value || null })
              checkCity(values.zip_code, event.target.value)
            }}
          />
        </LeadOverviewField>
      </Col>
      <Col xs={24} sm={8}>
        <LeadOverviewField label={t("projectWorkspace.cards.location.state")}>
          <Select
            allowClear
            showSearch
            className={"w-full"}
            disabled={disabled}
            aria-label={ariaLabel(t("projectWorkspace.cards.location.state"))}
            optionFilterProp={"label"}
            options={stateOptions ?? []}
            value={values.state ?? undefined}
            onChange={(next) => onChange({ state: next ?? null })}
          />
        </LeadOverviewField>
      </Col>
      <Col xs={24} sm={8}>
        <LeadOverviewField label={t("projectWorkspace.cards.location.zip")}>
          <TextInput
            disableAutoCapitalize
            disabled={disabled}
            aria-label={ariaLabel(t("projectWorkspace.cards.location.zip"))}
            value={values.zip_code ?? ""}
            onChange={(event) => {
              onChange({ zip_code: event.target.value || null })
              lookupZip(event.target.value)
            }}
          />
          <ZipLookupHint
            status={status}
            onChoose={chooseMatch}
            onDecline={declineCityMismatch}
          />
        </LeadOverviewField>
      </Col>
    </>
  )
}

interface LocationEditBodyProps {
  values: LocationCardValues
  setValues: (patch: Partial<LocationCardValues>) => void
  saving: boolean
  linked: boolean
  customerLocked: boolean
  knownStates: string[]
  stateOptions: AddressEditorProps["stateOptions"]
  applyCustomerAddress: (customer: AddressLike) => Partial<LocationCardValues>
}

type AddressLink = "none" | "customer" | "property"

/**
 * Rendered only while the card is in edit mode, so its `link` state resets to
 * "none" every time editing reopens: the persisted `use_customer_address` never
 * locks an editor on open, and re-linking either direction is an explicit tick.
 */
function LocationEditBody({
  values,
  setValues,
  saving,
  linked,
  customerLocked,
  knownStates,
  stateOptions,
  applyCustomerAddress
}: LocationEditBodyProps) {
  const { t } = useTranslation()
  const [link, setLink] = useState<AddressLink>("none")

  const applyPropertyAddress = (
    current: LocationCardValues
  ): Partial<LocationCardValues> => ({
    customer: {
      ...current.customer,
      address: current.address ?? null,
      address_2: current.address_2 ?? null,
      city: current.city ?? null,
      state: current.state ?? null,
      zip_code: current.zip_code ?? null
    },
    use_customer_address: true
  })

  // A live link needs its source Address 1 filled; clearing it would unmount
  // the checkbox and strand the mirrored editor locked with no way to untick.
  useEffect(() => {
    if (
      (link === "customer" && !values.customer.address) ||
      (link === "property" && !values.address)
    ) {
      setLink("none")
      setValues({ use_customer_address: false })
    }
  }, [values.address, values.customer.address, link, setValues])

  const forwardLinked = link === "customer"
  const reverseLinked = link === "property"

  return (
    <Row gutter={[12, 8]}>
      {linked && !customerLocked && !!values.address && link !== "customer" && (
        <Col xs={24}>
          <Checkbox
            disabled={saving}
            checked={reverseLinked}
            onChange={(event) => {
              if (event.target.checked) {
                setLink("property")
                setValues(applyPropertyAddress(values))
              } else {
                setLink("none")
                setValues({ use_customer_address: false })
              }
            }}
          >
            {t("projectWorkspace.cards.location.applyPropertyAddress")}
          </Checkbox>
        </Col>
      )}
      <AddressEditor
        // Remount on a reverse link/unlink so the ZIP hook re-reads the ZIP a
        // copied property address left in the fields, mirroring the property
        // editor's own key below. Namespaced so it never collides with that
        // sibling's `String(forwardLinked)`.
        key={`customer-${reverseLinked}`}
        title={t("projectWorkspace.cards.location.customerAddress")}
        values={values.customer}
        disabled={saving || customerLocked || reverseLinked}
        knownStates={knownStates}
        stateOptions={stateOptions}
        ariaPrefix={t("projectWorkspace.cards.customer.title")}
        onChange={(patch) => {
          const nextCustomer = { ...values.customer, ...patch }
          setValues({
            customer: nextCustomer,
            ...(forwardLinked
              ? applyCustomerAddress(nextCustomer)
              : { use_customer_address: false })
          })
        }}
      />
      {linked && !!values.customer.address && link !== "property" && (
        <Col xs={24}>
          <Checkbox
            disabled={saving}
            checked={forwardLinked}
            onChange={(event) => {
              if (event.target.checked) {
                setLink("customer")
                setValues(applyCustomerAddress(values.customer))
              } else {
                setLink("none")
                setValues({ use_customer_address: false })
              }
            }}
          >
            {t("projectWorkspace.cards.location.applyCustomerAddress")}
          </Checkbox>
        </Col>
      )}
      <AddressEditor
        key={String(forwardLinked)}
        title={t("projectWorkspace.cards.location.propertyAddress")}
        values={values}
        disabled={saving || forwardLinked}
        knownStates={knownStates}
        stateOptions={stateOptions}
        autoFocus
        onChange={(patch) => {
          const nextProperty = { ...values, ...patch }
          setValues({
            ...patch,
            ...(reverseLinked
              ? applyPropertyAddress(nextProperty)
              : { use_customer_address: false })
          })
        }}
      />
    </Row>
  )
}

function ProjectLocationCard({
  project,
  filterOptions,
  canEdit,
  onSave
}: ProjectLocationCardProps) {
  const { t } = useTranslation()
  const knownStates = useKnownStates(filterOptions)
  const linked = isLinkedProjectCustomer(project)
  const customerLocked = isCustomerAddressLocked(project)

  const values: LocationCardValues = {
    customer: projectCustomerAddressValues(project),
    ...projectPropertyAddressValues(project),
    use_customer_address: !!project?.use_customer_address
  }

  const applyCustomerAddress = (
    customer: AddressLike
  ): Partial<LocationCardValues> => ({
    address: customer.address ?? null,
    address_2: customer.address_2 ?? null,
    city: customer.city ?? null,
    state: customer.state ?? null,
    zip_code: customer.zip_code ?? null,
    use_customer_address: true
  })

  const saveLocation = (changed: Partial<LocationCardValues>) => {
    const patch: Partial<ProjectUpdateData> = {
      ...changed
    } as Partial<ProjectUpdateData>

    if (changed.customer) {
      const edited = pickChangedFields(values.customer, changed.customer)
      if (Object.keys(edited).length) {
        patch.customer = edited as ProjectUpdateData["customer"]
      } else {
        delete patch.customer
      }
    }

    if (!Object.keys(patch).length) return Promise.resolve(true)
    return onSave(patch)
  }

  return (
    <WorkspaceCard<LocationCardValues>
      cardId={"project-location"}
      title={
        <OverviewCardTitle icon={<EnvironmentOutlined />} accent={"indigo"}>
          {t("projectWorkspace.cards.location.title")}
        </OverviewCardTitle>
      }
      ariaTitle={t("projectWorkspace.cards.location.title")}
      className={"h-full"}
      canEdit={canEdit}
      values={values}
      onSave={saveLocation}
      validate={({ customer, zip_code }) =>
        (!customer.zip_code || zipCodeRegexUS.test(customer.zip_code)) &&
        (!zip_code || zipCodeRegexUS.test(zip_code))
      }
      renderEdit={({ values, setValues, saving }) => (
        <LocationEditBody
          values={values}
          setValues={setValues}
          saving={saving}
          linked={linked}
          customerLocked={customerLocked}
          knownStates={knownStates}
          stateOptions={filterOptions?.state}
          applyCustomerAddress={applyCustomerAddress}
        />
      )}
    >
      <Row gutter={[16, 12]} align={"stretch"}>
        <Col xs={24} md={16}>
          <div className={"flex flex-col justify-center gap-3 h-full"}>
            <Row gutter={[16, 8]}>
              <Col span={12} className={"border-r border-border"}>
                <LeadOverviewField
                  label={t("projectWorkspace.cards.location.customerAddress")}
                  value={customerAddressLine(project)}
                />
              </Col>
              <Col span={12}>
                <LeadOverviewField
                  label={t("projectWorkspace.cards.location.propertyAddress")}
                  value={propertyAddressLine(project)}
                />
                <AddressQuickLinks
                  className={"mt-1"}
                  address={project?.address}
                  address_2={project?.address_2}
                  city={project?.city}
                  state={project?.state}
                  zip_code={project?.zip_code}
                  geolocation={project?.geolocation}
                />
              </Col>
            </Row>
          </div>
        </Col>
        <Col xs={24} md={8} className={"min-h-[160px]"}>
          <AddressMapPreview
            height={"100%"}
            className={"mb-0 h-full"}
            fullAddress={projectMapAddress(project)}
            emptyText={t("projectWorkspace.cards.location.noMap")}
          />
        </Col>
      </Row>
    </WorkspaceCard>
  )
}

export { ProjectLocationCard }
